import "react-image-crop/dist/ReactCrop.css";

import { Carousel } from "@mantine/carousel";
import {
  Button,
  FileInput,
  Flex,
  Modal,
  NumberInput,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import { useIsOnline, useTheme } from "src/hooks";
import { UserSettingsContainer } from "src/pages/Settings/AccountDetails/AccountDetails.styled";

import {
  ButtonsBox,
  Divider,
  Image,
  ImageBox,
  UploaderContainer,
  UploaderHeader,
} from "./ImageUploader.styled";
import { convertPdfToImages } from "./pdf.utils";
import { DateTimeDisp } from "../DateTime";
import { getYachtId, getUserId } from "src/utils/localStorage";
import { usePostApi } from "src/hooks/usePostApi";

interface Props {
  image: string;
  setImage: (sign: string) => void;
  handleSave?: () => void;
  isLoading?: boolean;
  isStamp?: boolean;
  description?: string;
  lastUploadedDate?: {
    lt: string;
    utc: string;
  };
}

interface PDFCrop extends Crop {
  page?: number;
}

const ImageUploader = ({
  image,
  setImage,
  isStamp,
  handleSave,
  isLoading = false,
  description,
  lastUploadedDate,
}: Props) => {
  const theme = useTheme() as any;
  const [opened, { open, close }] = useDisclosure(false);
  const [crop, setCrop] = useState<PDFCrop>();
  const [loading, setLoading] = useState(false);
  const [pdfPages, setPdfPages] = useState<any>([]);
  const [embla, setEmbla] = useState(null);
  const filePickerRef = useRef<HTMLButtonElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isOnline = useIsOnline();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { post } = usePostApi();

  const handleUpload = () => {
    if (filePickerRef.current) {
      filePickerRef.current.click();
    }
  };

  const selectImage = async (file: File | null) => {
    if (file) {
      setLoading(true);

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImage(reader.result?.toString() ?? "");
      });

      if (file.type.split("/")[0] === "image") {
        reader.readAsDataURL(file);
      } else if (file.type === "application/pdf") {
        const pages = await convertPdfToImages(file);
        setPdfPages(pages);
      }
      setCrop(undefined);
      open();
      setLoading(false);
    }
  };

  useEffect(() => {
    setTimeout(() => embla?.reInit(), 1000);
  }, [opened, embla]);

  const recordSignatureHistory = async () => {
    if (!isOnline) return;
    await post({
      request: "record-user-hist",
      body: {
        params: {
          userid: getUserId(),
          action: "signature",
        },
      },
    });
  };

  const cropImageNow = () => {
    if (imgRef.current) {
      const canvas = document.createElement("canvas");
      const targetWidth = crop ? crop.width : 189;
      const targetHeight = crop ? crop.height : 113;
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        const pixelRatio = window.devicePixelRatio;
        canvas.width = targetWidth * pixelRatio;
        canvas.height = targetHeight * pixelRatio;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = "high";

        if (crop) {
          ctx.drawImage(
            imgRef.current,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height,
          );
        } else {
          const imgRatio =
            imgRef.current.naturalWidth / imgRef.current.naturalHeight;
          const targetRatio = targetWidth / targetHeight;

          let sourceWidth = imgRef.current.naturalWidth;
          let sourceHeight = imgRef.current.naturalHeight;
          let sourceX = 0;
          let sourceY = 0;

          if (imgRatio > targetRatio) {
            sourceWidth = sourceHeight * targetRatio;
            sourceX = (imgRef.current.naturalWidth - sourceWidth) / 2;
          } else {
            sourceHeight = sourceWidth / targetRatio;
            sourceY = (imgRef.current.naturalHeight - sourceHeight) / 2;
          }

          ctx.drawImage(
            imgRef.current,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            targetWidth,
            targetHeight,
          );
        }

        const base64Image = canvas.toDataURL("image/jpeg");
        setImage(base64Image);
        recordSignatureHistory();
        handleClose();
      }
    }
  };
  const handleClose = () => {
    close();
    setTimeout(() => {
      setPdfPages([]);
    }, 500);
  };

  const handleGoToPage = (page: number) => {
    if (embla) embla.scrollTo(page - 1);
  };

  useEffect(() => {
    if (embla) {
      const onSelect = () => {
        setCurrentIndex(embla.selectedScrollSnap());
      };
      embla.on("select", onSelect);
      onSelect(); // Set initial index
    }
  }, [embla]);
  return (
    <UserSettingsContainer
      style={
        isStamp
          ? {
              border: "none",
            }
          : undefined
      }>
      <UploaderContainer>
        {description && (
          <Text size="xs" c="red">
            {description}
          </Text>
        )}
        <UploaderHeader>
          <Text size="sm">{isStamp ? "Stamp" : "Signature"}</Text>
          {isOnline && (
            <>
              <ButtonsBox>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleUpload}
                  loading={loading}>
                  Upload
                </Button>
                {!isStamp && (
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => handleSave && handleSave()}
                    loading={isLoading}>
                    Save
                  </Button>
                )}
              </ButtonsBox>
              <FileInput
                ref={filePickerRef}
                display="none"
                accept="image/*, application/pdf"
                onChange={selectImage}
              />
            </>
          )}
        </UploaderHeader>
        {image && (
          <>
            <Divider />
            <ImageBox>
              <Image src={image} />
            </ImageBox>
            {lastUploadedDate && (
              <Flex justify="center" align="center" gap={4}>
                <Text ta="center" size="xs" c="dimmed">
                  Last Uploaded:
                </Text>
                <DateTimeDisp
                  fz={12}
                  color="dimmed"
                  dir="row"
                  lt={lastUploadedDate.lt}
                  utc={lastUploadedDate.utc}
                />
              </Flex>
            )}
          </>
        )}
      </UploaderContainer>

      <Modal
        opened={opened}
        onClose={cropImageNow}
        title="Image Cropper"
        size={pdfPages.length > 0 ? "xl" : "md"}>
        <div style={{ position: "relative" }}>
          {image && pdfPages.length === 0 && (
            <ReactCrop crop={crop} onChange={setCrop}>
              <img ref={imgRef} src={image} />
            </ReactCrop>
          )}
          {pdfPages.length > 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  marginBottom: 20,
                  gap: 10,
                }}>
                <p style={{ color: theme.colors.main[9] }}>Page:</p>
                <NumberInput
                  value={currentIndex + 1}
                  onChange={v => handleGoToPage(Number(v))}
                  style={{ width: "80px" }}
                />
                <p style={{ opacity: 0.5 }}>/ {pdfPages.length}</p>
              </div>

              <Carousel
                withIndicators
                height={600}
                getEmblaApi={setEmbla}
                emblaOptions={{ watchDrag: false }}>
                {pdfPages.map((page: string, index: number) => (
                  <Carousel.Slide
                    key={index}
                    style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                      <ReactCrop
                        crop={crop?.page === index ? crop : undefined}
                        onChange={v => {
                          setCrop({ ...v, page: index });
                        }}>
                        <img
                          src={page}
                          ref={crop?.page === index ? imgRef : undefined}
                          alt=""
                          width="600"
                          style={{ height: "auto" }}
                        />
                      </ReactCrop>
                    </div>
                  </Carousel.Slide>
                ))}
              </Carousel>
            </div>
          )}
        </div>
        <Button onClick={cropImageNow} disabled={!crop && pdfPages.length > 0}>
          {!crop && pdfPages.length === 0 ? "Auto Crop" : "Crop"}
        </Button>
      </Modal>
    </UserSettingsContainer>
  );
};

export default ImageUploader;
